// ─── src/chat/auraLinkText.js ────────────────────────────────────────────────
// Decides which parts of an Aura reply become clickable. Kept separate from the
// component so it can be tested directly: what this function will and will not
// turn into a link is a security boundary, not a styling choice.
//
// Aura's reply is model-generated and a couple's message reaches her prompt, so
// the rules are deliberately narrow:
//
//   · INTERNAL PATHS ONLY — a path beginning with one slash can only land on our
//     own routes. External URLs are never linkified: one in her output either came
//     from a model that guessed it or from something a couple typed, and neither
//     earns a click.
//   · The text is SPLIT, never parsed. Every segment is returned as data for the
//     component to render as a React child, so fabricated markup stays inert text.
//
// The first version matched /shop/wedding inside https://example.com/shop/wedding
// and would have made an attacker-supplied domain look like an in-app link. That is
// why URLs are removed from consideration first rather than guarded by a lookbehind
// — lookbehind is also a parse error in older Safari, which would take the whole
// chat down for a cosmetic feature.

const URL_LIKE = /(https?:\/\/\S+|www\.\S+)/gi;

// Conservative on purpose: the routes we actually emit, not "any slash-word", which
// would turn ordinary prose like "indoor/outdoor" into a broken link.
const INTERNAL_PATH = /(\/(?:shop|lwd-edit|the-edit|publications|showcase|destinations)(?:\/[a-z0-9-]+)*)/gi;

// Trailing punctuation belongs to the sentence, not the URL. Without this, a path
// ending a sentence links to "/shop/wedding/shoes." and 404s on its first use.
const TRAILING = /[.,;:!?)\]]+$/;

/**
 * @param {string} text
 * @returns {Array<{ type: 'text' | 'link', value: string, trailing?: string }>}
 */
export function linkSegments(text) {
  if (!text || typeof text !== 'string') return [];

  const out = [];
  // Pass 1: isolate anything URL-shaped so pass 2 cannot reach inside it.
  for (const [i, chunk] of text.split(URL_LIKE).entries()) {
    if (!chunk) continue;
    if (i % 2 === 1) { out.push({ type: 'text', value: chunk }); continue; }

    // Pass 2: internal paths within ordinary prose.
    for (const [j, part] of chunk.split(INTERNAL_PATH).entries()) {
      if (!part) continue;
      if (j % 2 === 0) { out.push({ type: 'text', value: part }); continue; }
      const trailing = (part.match(TRAILING) ?? [''])[0];
      out.push({
        type: 'link',
        value: trailing ? part.slice(0, -trailing.length) : part,
        trailing,
      });
    }
  }
  return out;
}
